import { apiFetch } from "@/lib/api";

const BASE = "/api/inside-sales/wireless-portal-accounts";

export type WirelessPortalAccountColumn =
  | "needs_review"
  | "missing_admin"
  | "ready"
  | "failed"
  | "done";

export interface WirelessPortalUnshippedSalesOrder {
  salesOrderId: string;
  salesOrderNumber: string;
  shipByDate: string | null;
}

export interface WirelessPortalAccountItem {
  id: string;
  column: WirelessPortalAccountColumn;
  salesOrderId: string;
  salesOrderNumber: string;
  endUserFsAccountId: string;
  endUserName: string | null;
  freshsalesAccountId: string;
  portalOrganizationId: number | null;
  portalOrganizationName: string | null;
  newDevicesGroupId: number | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  earliestWirelessShipByDate: string | null;
  unshippedSalesOrders: WirelessPortalUnshippedSalesOrder[];
  wirelessShipByDates: string[];
  orderShipByDate: string | null;
  manufacturingShipByDates: string[];
  hasPortalAdmin: boolean | null;
  portalAdminCount: number | null;
  portalAdminSummary: string | null;
  defaultAdminTimeZone: string;
  defaultAdminTimeZoneHint: string;
}

export interface WirelessPortalAdminTimeZoneOption {
  value: string;
  label: string;
}

export const INSIDE_SALES_WIRELESS_PORTAL_ACCOUNTS_QUERY_KEY = [
  "inside-sales",
  "wireless-portal-accounts",
] as const;

export function fetchInsideSalesWirelessPortalAccounts(includeDone = false): Promise<{
  accounts: WirelessPortalAccountItem[];
  adminTimeZoneOptions: WirelessPortalAdminTimeZoneOption[];
}> {
  const params = new URLSearchParams();
  if (includeDone) {
    params.set("includeDone", "true");
  }
  const query = params.toString();
  return apiFetch<{
    accounts: WirelessPortalAccountItem[];
    adminTimeZoneOptions: WirelessPortalAdminTimeZoneOption[];
  }>(query ? `${BASE}?${query}` : BASE);
}

export function createInsideSalesWirelessPortalAdmin(
  accountId: string,
  body: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    timeZone: string;
  },
): Promise<{ email: string }> {
  return apiFetch<{ email: string }>(
    `${BASE}/${encodeURIComponent(accountId)}/create-admin`,
    { method: "POST", body },
  );
}

export interface WirelessPortalOrganizationSearchHit {
  organizationId: number;
  name: string;
  freshsalesAccountId: string | null;
  domain: string | null;
}

export function searchInsideSalesWirelessPortalOrganizations(
  query: string,
): Promise<{ organizations: WirelessPortalOrganizationSearchHit[] }> {
  const params = new URLSearchParams();
  params.set("q", query);
  return apiFetch<{ organizations: WirelessPortalOrganizationSearchHit[] }>(
    `${BASE}/portal-organizations?${params.toString()}`,
  );
}

export function updateInsideSalesWirelessPortalOrganization(
  accountId: string,
  body: { name: string; domain: string },
): Promise<{ name: string; domain: string | null }> {
  return apiFetch<{ name: string; domain: string | null }>(
    `${BASE}/${encodeURIComponent(accountId)}/update-organization`,
    { method: "POST", body },
  );
}

export function markInsideSalesWirelessPortalAccountDone(accountId: string): Promise<{
  account: WirelessPortalAccountItem;
}> {
  return apiFetch<{ account: WirelessPortalAccountItem }>(
    `${BASE}/${encodeURIComponent(accountId)}/mark-done`,
    { method: "POST" },
  );
}

export interface PendingPortalAdditionalUser {
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  role: string;
  accessLevel: number;
}

export interface PendingPortalUserItem {
  id: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  adminPhone: string | null;
  organizationName: string;
  additionalUsers: PendingPortalAdditionalUser[];
  createdAt: string;
  status: string;
}

export const INSIDE_SALES_PENDING_PORTAL_USERS_QUERY_KEY = [
  "inside-sales",
  "wireless-portal-pending-users",
] as const;

export function fetchInsideSalesPendingPortalUsers(
  status = "Pending",
): Promise<{ users: PendingPortalUserItem[] }> {
  const params = new URLSearchParams();
  params.set("status", status);
  return apiFetch<{ users: PendingPortalUserItem[] }>(`${BASE}/pending-users?${params.toString()}`);
}

export function pendingPortalRequestEmails(user: PendingPortalUserItem): string[] {
  return [user.adminEmail, ...user.additionalUsers.map((entry) => entry.email)].filter(
    (email) => email.trim() !== "",
  );
}

export function approveInsideSalesPendingPortalUser(
  userId: string,
  body: { organizationId: number; organizationName: string; timeZone: string },
): Promise<{ user: PendingPortalUserItem; createdEmails: string[] }> {
  return apiFetch<{ user: PendingPortalUserItem; createdEmails: string[] }>(
    `${BASE}/pending-users/${encodeURIComponent(userId)}/approve`,
    { method: "POST", body },
  );
}

export interface PendingPortalJoinPerson {
  email: string;
  alreadyInPortal: boolean;
  canCreate: boolean;
  errors: string[];
  warnings: string[];
}

export function previewInsideSalesPendingPortalUserOrganization(
  emails: string[],
  organizationId?: number,
): Promise<{
  ok: boolean;
  userCount: number;
  errors: string[];
  warnings: string[];
  people: PendingPortalJoinPerson[];
  creatableEmails: string[];
  message: string;
}> {
  const params = new URLSearchParams();
  params.set("emails", emails.join(","));
  if (organizationId != null && Number.isFinite(organizationId) && organizationId > 0) {
    params.set("organizationId", String(organizationId));
  }
  return apiFetch<{
    ok: boolean;
    userCount: number;
    errors: string[];
    warnings: string[];
    people: PendingPortalJoinPerson[];
    creatableEmails: string[];
    message: string;
  }>(`${BASE}/pending-users/review?${params.toString()}`);
}

export interface AddPortalUserContext {
  organizationId: number;
  organizationName: string;
  hasPortalAdmin: boolean;
  timeZone: string;
  timeZoneHint: string;
}

export function fetchInsideSalesAddPortalUserContext(
  organizationId: number,
): Promise<AddPortalUserContext> {
  return apiFetch<AddPortalUserContext>(
    `${BASE}/portal-organizations/${encodeURIComponent(String(organizationId))}/add-user-context`,
  );
}

export function addInsideSalesPortalUser(body: {
  organizationId: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  timeZone: string;
  accessLevel: number;
}): Promise<{ email: string; accessLevel: number }> {
  return apiFetch<{ email: string; accessLevel: number }>(
    `${BASE}/portal-users`,
    { method: "POST", body },
  );
}

export function denyInsideSalesPendingPortalUser(userId: string): Promise<{ user: PendingPortalUserItem }> {
  return apiFetch<{ user: PendingPortalUserItem }>(
    `${BASE}/pending-users/${encodeURIComponent(userId)}/deny`,
    { method: "POST" },
  );
}
